"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Track = exports.TrackingProvider = exports.TrackingContext = void 0;
var react_1 = __importDefault(require("react"));
var react_dom_1 = __importDefault(require("react-dom"));
var prop_types_1 = __importDefault(require("prop-types"));
var index_1 = __importDefault(require("./index"));
/**
 * @description React context that is used for scoping Segment event properties
 * to a subtree of components. This context is used by components like
 * {@linkcode TrackingProvider} and {@linkcode Track}.
 */
exports.TrackingContext = react_1.default.createContext({});
/**
 * @description Used to wrap a functional component in a class
 * so we can attach a ref to it.
 *
 * @ignore
 */
var TrackClassWrapper = /** @class */ (function (_super) {
    __extends(TrackClassWrapper, _super);
    function TrackClassWrapper() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    TrackClassWrapper.prototype.render = function () {
        return this.props.children;
    };
    return TrackClassWrapper;
}(react_1.default.PureComponent));
/**
 * @description A React context provider component that allows a map of
 * properties to be applied to Segment events dispatched by {@linkcode Track}
 * components and components using the {@linkcode track} or
 * {@linkcode withTrack} decorators, or the {@linkcode useTrack} hook, in its
 * child component tree.
 *
 * Use this component to scope specific Segment properties to a subset of your
 * application's component tree. Multiple layers of this provider can be
 * used in the same component tree.
 *
 * This component can also be added to your component using the
 * {@linkcode trackingProvider} decorator.
 *
 * Example:
 *
 * ```jsx
 * import { TrackingProvider, Track } from '@optimizely/segment-sdk-wrapper';
 *
 * const app = (
 *   <TrackingProvider properties={{ appName: 'FLAGS', pageName: 'NONE' }}>
 *     <Route path="/flags">
 *       <TrackingProvider properties={{ pageName: 'DASHBOARD' }}>
 *         <Track eventName="opened_rollout">...</Track>
 *       </TrackingProvider>
 *     </Route>
 *   </TrackingProvider>
 * );
 *
 * // Here, the <Track> component will receive the properties:
 * // { appName: 'FLAGS', pageName: 'DASHBOARD' }
 * //
 * // Notice that properties set by higher-level <TrackingProvider> instances
 * // can be overwritten by lower-level <TrackingProvider> instances in their
 * // descendant component trees, like `pageName` in this example.
 * ```
 *
 * Note: The {@linkcode TrackingProvider} only provides Segment property context
 * to other {@linkcode TrackingProvider} instances as well as {@linkcode Track}
 * instances. It does not provide properties to {@linkcode SegmentSDKWrapper}
 * method calls made from within its component tree.
 *
 * Note: The properties provided by {@linkcode TrackingProvider} can be
 * overwritten by descendant {@linkcode Track} instances using the prop
 * {@link TrackProps.properties}.
 *
 * @category Component
 */
var TrackingProvider = /** @class */ (function (_super) {
    __extends(TrackingProvider, _super);
    function TrackingProvider() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    TrackingProvider.prototype.render = function () {
        var _this = this;
        var properties = this.props.properties;
        return (react_1.default.createElement(exports.TrackingContext.Consumer, null, function (val) { return (react_1.default.createElement(exports.TrackingContext.Provider, { value: __assign(__assign({}, val), properties) }, _this.props.children)); }));
    };
    TrackingProvider.propTypes = {
        properties: prop_types_1.default.object,
    };
    TrackingProvider.defaultProps = {
        properties: {},
    };
    return TrackingProvider;
}(react_1.default.Component));
exports.TrackingProvider = TrackingProvider;
/**
 * @description A React component that automatically tracks DOM events and sends
 * them to Segment. Given a DOM event type specified in
 * {@link TrackProps.eventType}, this component can attach event listeners to
 * either its direct children or its entire descendant element tree.
 *
 * This component can also be added to your component using the
 * {@linkcode track} and {@linkcode withTrack} decorators. This component can
 * be replaced in functional components using the {@linkcode useTrack} hook.
 *
 * This component is compatible with the {@linkcode TrackingProvider} component
 * and the {@linkcode trackingProvider} decorator, and will receive any
 * properties defined in {@linkcode TrackingProvider} ancestor instances.
 *
 * Example:
 *
 * ```jsx
 * import { Track } from '@optimizely/segment-sdk-wrapper';
 *
 * <Track eventName="interacted_with_rollout_row_in_table" eventType="click">
 *   <a href="/rollouts/12345">My Awesome Feature Flag</a>
 *   <button onClick={deleteRollout}>Delete</button>
 * </Track>
 * ```
 *
 * @category Component
 */
var Track = /** @class */ (function (_super) {
    __extends(Track, _super);
    function Track() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.trackedRefs = [];
        /**
         * @description Helper function to find the next element sibling of this HTML element.
         * This logic follows the React Fiber node linked-list of element
         * siblings to determine if an element has an adjacent sibling element.
         */
        _this.getNextElementSibling = function (DOMNode) {
            var nextNode = null;
            for (var key in DOMNode) {
                /**
                 * We'll try our best to find any subsequent element siblings by peeking
                 * into the element's React Fiber node and checking the sibling property.
                 *
                 * This is a little hacky, but its implemented defensively and is the best
                 * we can do to handle wrapping Components whose children are opaque to us.
                 * Note: Verify/update this with the next major release of React (17+).
                 *
                 * More about Fiber here:
                 *   https://github.com/acdlite/react-fiber-architecture#child-and-sibling
                 */
                if (Object.hasOwnProperty.apply(DOMNode, [key]) &&
                    key.startsWith("__reactInternalInstance$")) {
                    nextNode = DOMNode[key].sibling && DOMNode.nextElementSibling;
                    break;
                }
            }
            return nextNode;
        };
        /**
         * @description Event handler which invokes track with any additional properties passed,
         * either from the parent context or via its own properties prop.
         */
        _this.handleEvent = function (parentProperties) {
            var _a = _this.props, properties = _a.properties, options = _a.options;
            index_1.default.track(_this.props.eventName, __assign(__assign({}, properties), parentProperties), options);
        };
        /**
         * @description Handler for a callback React ref.
         * Binds an event listener to the element and attempts to do the same to any React element siblings.
         * Also invokes the existing ref if necessary.
         */
        _this.handleChildMounted = function (el, component, parentProperties, withSiblings) {
            var eventType = _this.props.eventType;
            if (_this.trackedRefs.indexOf(el) !== -1) {
                // Bail if we're already aware of this ref.
                return;
            }
            var DOMNode = react_dom_1.default.findDOMNode(el);
            while (DOMNode && eventType !== undefined) {
                DOMNode.addEventListener(eventType, _this.handleEvent.bind(_this, parentProperties));
                // Since findDOMNode only returns the first child of el, we must find any siblings ourselves.
                DOMNode = (withSiblings && _this.getNextElementSibling(DOMNode)) || null;
            }
            if (component && typeof component.ref === "function") {
                component.ref(el);
            }
            if (el) {
                _this.trackedRefs.push(el);
            }
        };
        /**
         * @description Method to traverse all children (optionally recursively) in search of all top
         * level React DOM elements. These are the elements to which our event
         * handler should be attached, as "custom" Components cannot listen to events.
         */
        _this.remapChildren = function (children, parentProperties) {
            var remapChildren = _this.props.remapChildren;
            return react_1.default.Children.map(children, function (child) {
                var getRef = function (withSiblings) { return function (el) {
                    return _this.handleChildMounted(el, child, parentProperties, withSiblings);
                }; };
                if (!child ||
                    typeof child !== "object" ||
                    !("type" in child) ||
                    !child.type) {
                    // Text node that we cant track.
                    return child;
                }
                else if (typeof child.type === "string") {
                    // DOM Component, such as: <button />
                    return react_1.default.cloneElement(child, { ref: getRef(false) });
                }
                else if (remapChildren &&
                    react_1.default.Children.count(child.props.children)) {
                    /**
                     * Custom Component w/props.children, such as:
                     *   <MyComponent ... />
                     *     <.../>
                     *     <.../>
                     *   </MyComponent>
                     */
                    return react_1.default.cloneElement(child, {
                        children: _this.remapChildren(child.props.children, parentProperties),
                    });
                }
                else if (child.type.prototype && child.type.prototype.render) {
                    // Custom Class Component w/o props.children, such as: <MyClassComponent ... />
                    return react_1.default.cloneElement(child, { ref: getRef(true) });
                }
                else {
                    // Custom Function Component w/o props.children, such as: <MyFunctionComponent ... />
                    return react_1.default.createElement(TrackClassWrapper, { ref: getRef(true) }, child);
                }
            });
        };
        return _this;
    }
    Track.prototype.render = function () {
        var _this = this;
        return (react_1.default.createElement(exports.TrackingContext.Consumer, null, function (properties) {
            return _this.remapChildren(_this.props.children, properties);
        }));
    };
    Track.propTypes = {
        eventType: prop_types_1.default.string,
        eventName: prop_types_1.default.string.isRequired,
        options: prop_types_1.default.object,
        properties: prop_types_1.default.object,
        remapChildren: prop_types_1.default.bool,
    };
    Track.defaultProps = {
        eventType: "click",
        options: {},
        properties: {},
        remapChildren: false,
    };
    return Track;
}(react_1.default.Component));
exports.Track = Track;
exports.default = {
    TrackingContext: exports.TrackingContext,
    TrackingProvider: TrackingProvider,
    Track: Track,
};
